import { lazy, Suspense } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import Loading3D from './components/3d/Loading3D';
import { useAuthStore } from './hooks/useAuthStore';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Auth pages
const Login = lazy(() => import('./pages/auth/Login'));
const LandingPage = lazy(() => import('./pages/landing/LandingPage'));

// Dashboard pages
const OwnerDashboard = lazy(() => import('./pages/dashboard/OwnerDashboard'));
const ManagerDashboard = lazy(() => import('./pages/dashboard/ManagerDashboard'));
const AccountantDashboard = lazy(() => import('./pages/dashboard/AccountantDashboard'));
const TeacherDashboard = lazy(() => import('./pages/dashboard/TeacherDashboard'));
const ParentDashboard = lazy(() => import('./pages/dashboard/ParentDashboard'));

// User management
const UserManagementPage = lazy(() => import('./pages/users/UserManagementPage'));

// Student pages
const StudentList = lazy(() => import('./pages/students/StudentList'));
const StudentDetail = lazy(() => import('./pages/students/StudentDetail'));
const StudentForm = lazy(() => import('./pages/students/StudentForm'));

// Finance pages
const InvoiceList = lazy(() => import('./pages/finance/InvoiceList'));
const PaymentList = lazy(() => import('./pages/finance/PaymentList'));
const ExpenseList = lazy(() => import('./pages/finance/ExpenseList'));
const SalaryList = lazy(() => import('./pages/finance/SalaryList'));

// Other pages
const AttendancePage = lazy(() => import('./pages/attendance/AttendancePage'));
const ExamList = lazy(() => import('./pages/exams/ExamList'));
const GradeEntry = lazy(() => import('./pages/exams/GradeEntry'));
const SchedulePage = lazy(() => import('./pages/academics/SchedulePage'));
const ClassList = lazy(() => import('./pages/academics/ClassList'));
const ReportPage = lazy(() => import('./pages/reports/ReportPage'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));
const ParentPortal = lazy(() => import('./pages/parents/ParentPortal'));
const BranchList = lazy(() => import('./pages/branches/BranchList'));
const NotFound = lazy(() => import('./pages/NotFound'));

/** Role-based dashboard redirect */
function RoleDashboard() {
  const { user } = useAuthStore();
  const role = user?.role;

  switch (role) {
    case 'owner':
    case 'superadmin':
      return <OwnerDashboard />;
    case 'school_manager':
    case 'admin':
      return <ManagerDashboard />;
    case 'branch_manager':
    case 'principal':
      return <ManagerDashboard />;
    case 'finance_manager':
      return <AccountantDashboard />;
    case 'accountant':
      return <AccountantDashboard />;
    case 'teacher':
      return <TeacherDashboard />;
    case 'parent':
      return <ParentDashboard />;
    default:
      return <Navigate to="/login" replace />;
  }
}

/** Protected route wrapper with hierarchy-based role checks */
function ProtectedRoute({ children, allowedRoles }: { children: React.ReactNode; allowedRoles?: string[] }) {
  const { isAuthenticated, user, isRoleOrAbove } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && user?.role) {
    // Check if user's role is in allowed list OR if user is owner (can access everything)
    const hasAccess = allowedRoles.includes(user.role) || user.role === 'owner' || user.role === 'superadmin';
    if (!hasAccess) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<Login />} />

      {/* Dashboard - Role Based */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <RoleDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="/dashboard/owner" element={<ProtectedRoute allowedRoles={['owner', 'superadmin']}><OwnerDashboard /></ProtectedRoute>} />
      <Route path="/dashboard/manager" element={<ProtectedRoute allowedRoles={['owner', 'superadmin', 'school_manager', 'admin', 'branch_manager', 'principal']}><ManagerDashboard /></ProtectedRoute>} />
      <Route path="/dashboard/accountant" element={<ProtectedRoute allowedRoles={['owner', 'superadmin', 'school_manager', 'admin', 'branch_manager', 'principal', 'finance_manager', 'accountant']}><AccountantDashboard /></ProtectedRoute>} />
      <Route path="/dashboard/teacher" element={<ProtectedRoute allowedRoles={['owner', 'superadmin', 'school_manager', 'admin', 'branch_manager', 'principal', 'finance_manager', 'accountant', 'teacher']}><TeacherDashboard /></ProtectedRoute>} />
      <Route path="/dashboard/parent" element={<ProtectedRoute allowedRoles={['parent']}><ParentDashboard /></ProtectedRoute>} />

      {/* User Management - hierarchy aware */}
      <Route path="/dashboard/users" element={<ProtectedRoute><UserManagementPage /></ProtectedRoute>} />

      {/* Students Module */}
      <Route path="/students" element={<ProtectedRoute allowedRoles={['owner', 'manager', 'teacher']}><StudentList /></ProtectedRoute>} />
      <Route path="/students/:id" element={<ProtectedRoute allowedRoles={['owner', 'manager', 'teacher', 'parent']}><StudentDetail /></ProtectedRoute>} />
      <Route path="/students/new" element={<ProtectedRoute allowedRoles={['owner', 'manager']}><StudentForm /></ProtectedRoute>} />

      {/* Finance Module */}
      <Route path="/finance/invoices" element={<ProtectedRoute allowedRoles={['owner', 'manager', 'accountant']}><InvoiceList /></ProtectedRoute>} />
      <Route path="/finance/payments" element={<ProtectedRoute allowedRoles={['owner', 'manager', 'accountant']}><PaymentList /></ProtectedRoute>} />
      <Route path="/finance/expenses" element={<ProtectedRoute allowedRoles={['owner', 'manager', 'accountant']}><ExpenseList /></ProtectedRoute>} />
      <Route path="/finance/salaries" element={<ProtectedRoute allowedRoles={['owner', 'manager', 'accountant']}><SalaryList /></ProtectedRoute>} />

      {/* Attendance Module */}
      <Route path="/attendance" element={<ProtectedRoute allowedRoles={['owner', 'manager', 'teacher']}><AttendancePage /></ProtectedRoute>} />

      {/* Exams Module */}
      <Route path="/exams" element={<ProtectedRoute allowedRoles={['owner', 'manager', 'teacher']}><ExamList /></ProtectedRoute>} />
      <Route path="/exams/grades" element={<ProtectedRoute allowedRoles={['owner', 'manager', 'teacher']}><GradeEntry /></ProtectedRoute>} />

      {/* Academics Module */}
      <Route path="/academics/schedule" element={<ProtectedRoute allowedRoles={['owner', 'manager', 'teacher']}><SchedulePage /></ProtectedRoute>} />
      <Route path="/academics/classes" element={<ProtectedRoute allowedRoles={['owner', 'manager']}><ClassList /></ProtectedRoute>} />

      {/* Reports Module */}
      <Route path="/reports" element={<ProtectedRoute allowedRoles={['owner', 'manager', 'accountant']}><ReportPage /></ProtectedRoute>} />

      {/* Settings Module */}
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />

      {/* Parent Portal */}
      <Route path="/parents" element={<ProtectedRoute allowedRoles={['parent']}><ParentPortal /></ProtectedRoute>} />

      {/* Branches Module */}
      <Route path="/branches" element={<ProtectedRoute allowedRoles={['owner']}><BranchList /></ProtectedRoute>} />

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <HashRouter>
        <Suspense fallback={<Loading3D />}>
          <AppRoutes />
        </Suspense>
      </HashRouter>
      <Toaster position="top-left" richColors closeButton toastOptions={{ style: { fontFamily: 'Cairo, Inter, sans-serif' } }} />
    </QueryClientProvider>
  );
}

export default App;
